<template>
  <v-row class="dashboard-row">
    <!-- Main Dashboard Component -->
    <!-- Displays comprehensive member and transaction summary -->
    <v-col cols="12" md="12">
      <dashboard-card-member-summary 
        :currentDate="currentDate" 
        :report="todayReport" 
        :members="memberInfo"
        :accountBanks="accountBankReport" 
        :depositTransaction="depositReport"
        :withdrawTransaction="withdrawReport">
      </dashboard-card-member-summary>
    </v-col>

    <!-- 
      ============================================
      DAILY SUMMARY REPORT TABLE
      ============================================
      ตารางแสดงสรุปรายวัน พร้อม dropdown เลือกเดือนย้อนหลัง
      
      PROPS ที่ส่งไป:
      - reportType="daily"      → บอก component ว่าเป็นรายงานรายวัน (ไม่ใช่ monthly)
      - :title                  → หัวข้อ "สรุปรายวัน" จาก i18n
      - :reports                → Array ข้อมูลรายวัน [{day, active_users, deposit, withdraw, profit}, ...]
      - :totalProfit            → กำไรรวมทั้งเดือน (แสดงด้านล่างตาราง)
      - :currentMonthYear       → เดือน-ปี ที่แสดง เช่น "11-25"
      
      PROPS ใหม่ (เพิ่มสำหรับ dropdown):
      - :monthOptions           → Array ตัวเลือก dropdown [{text: "พ.ย. 2568", value: {year, month}}, ...]
      - :defaultMonth           → ค่าเริ่มต้น dropdown {year: 2025, month: 11}
      - :loading                → Boolean แสดง loading ตอนโหลดข้อมูล
      
      EVENTS ใหม่:
      - @month-change           → เมื่อ user เปลี่ยนเดือน → เรียก onDailyMonthChange(selected)
    -->
    <v-col cols="12" md="6">
      <dashboard-card-summary-report 
        reportType="daily" 
        :title="$t('dashboard.block7_head')" 
        :reports="dailyReports" 
        :totalProfit="totalProfit" 
        :currentMonthYear="currentMonthYear"
        :monthOptions="dailyMonthOptions"
        :defaultMonth="dailyDefaultMonth"
        :loading="dailyLoading"
        @month-change="onDailyMonthChange"
      />
    </v-col>
    
    <!-- Monthly Summary Report Table -->
    <v-col cols="12" md="6">
      <dashboard-card-summary-report 
        reportType="monthly" 
        :title="$t('dashboard.block8_head')"
        :reports="monthlyReports" />
    </v-col>
  </v-row>
</template>

<script>
// ========== IMPORTS ==========
import axios from 'axios'
import { mdiPoll, mdiLabelVariantOutline, mdiCurrencyUsd, mdiHelpCircleOutline } from '@mdi/js'
import StatisticsCardVertical from '@/components/statistics-card/StatisticsCardVertical.vue'

// Dashboard component imports
import DashboardCardProfitsToday from './DashboardCardProfitsToday.vue'
import DashboardCardMembers from './DashboardCardMembers.vue'
import DashboardCardBanks from './DashboardCardBanks.vue'
import DashboardCardWithdraw from './DashboardCardWithdraw.vue'
import DashboardCardDeposit from './DashboardCardDeposit.vue'
import DashboardCardSummaryReport from './DashboardCardSummaryReport.vue'
import DashboardCardAffiliateTotalPlay from './DashboardCardAffiliateTotalPlay.vue'
import DashboardCongratulationJohn from './DashboardCongratulationJohn.vue'
import DashboardStatisticsCard from './DashboardStatisticsCard.vue'
import DashboardCardTotalEarning from './DashboardCardTotalEarning.vue'
import DashboardCardSalesByCountries from './DashboardCardSalesByCountries.vue'
import DashboardWeeklyOverview from './DashboardWeeklyOverview.vue'
import DashboardDatatable from './DashboardDatatable.vue'

// Main dashboard card component
import DashboardCardMemberSummary from './DashboardCardMemberSummary.vue'

// Utilities
import moment from 'moment-timezone'
import store from '@/store'
import { getFormattedTime } from '@/utils/dateTimeUtils'

/**
 * Dashboard.vue - Main Dashboard Component
 * 
 * This is the main dashboard container that:
 * 1. Fetches all dashboard data from multiple API endpoints
 * 2. Passes data to child components via props
 * 3. Handles bank code formatting for display
 * 4. Logs admin activity
 * 
 * IMPORTANT: This version does NOT have auto-refresh to prevent
 * performance issues. All data is fetched once on component creation.
 */
export default {
  name: 'Dashboard',
  
  // ========== COMPONENTS REGISTRATION ==========
  components: {
    StatisticsCardVertical,
    DashboardCardProfitsToday,
    DashboardCardMembers,
    DashboardCardBanks,
    DashboardCardWithdraw,
    DashboardCardDeposit,
    DashboardCardAffiliateTotalPlay,
    DashboardCardSummaryReport,
    DashboardCongratulationJohn,
    DashboardStatisticsCard,
    DashboardCardTotalEarning,
    DashboardCardSalesByCountries,
    DashboardWeeklyOverview,
    DashboardDatatable,
    DashboardCardMemberSummary,
  },
  
  // ========== DATA PROPERTIES ==========
  data() {
    return {
      // ===== TODAY'S REPORT DATA =====
      // Summary of today's transactions and member registrations
      todayReport: {
        total_amount_deposit: 0,   // Total deposit amount today
        total_amount_withdraw: 0,  // Total withdrawal amount today
        total_amount_profit: 0,    // Net profit today (deposit - withdrawal)
        total_member_register: 0,  // New member registrations today
        count_deposit: 0,          // Number of deposit transactions
        count_withdraw: 0,         // Number of withdrawal transactions
        total_amount_bonus: 0,     // Total bonus given today
        total_amount_game: 0,      // Total game rewards today
      },
      
      // ===== MEMBER INFO =====
      // Member statistics
      memberInfo: {
        all_member: 0,         // Total registered members
        member_deposit: 0,     // Members who have made deposits
        member_not_deposit: 0, // Members who haven't made deposits
      },
      
      // ===== TRANSACTION REPORTS =====
      accountBankReport: [],    // List of bank accounts with balances
      depositReport: [],        // Recent deposit transactions
      withdrawReport: [],       // Recent withdrawal transactions
      dailyReports: [],         // Daily summary for last 30 days
      monthlyReports: [],       // Monthly summary for last 12 months
      
      // ===== GAME STATISTICS =====
      // Affiliate and game play report
      affiliateTotalReport: {
        total_play_all: 0,    // Total amount played across all games
        total_play_sport: 0,  // Total amount played in sports betting
        total_play_slot: 0,   // Total amount played in slot games
        total_play_casino: 0, // Total amount played in casino games
      },
      
      // ===== OTHER DATA =====
      totalProfit: 0,         // Total profit for current month
      currentDate: '',        // Current date/time for display
      currentMonthYear: '',   // Current month-year format (MM-YY)
      bankSystem: '',         // Bank system code (TH, KR, etc.)
      tz: '',                 // Timezone
      // =====  Daily Summary Months selection =====
      dailyMonthOptions: [],
      dailyDefaultMonth: {},
      dailyLoading: false,
    }
  },
  
  // ========== LIFECYCLE HOOKS ==========
  /**
   * Created lifecycle hook
   * Executes when component is created
   * Loads all initial data sequentially
   */
  async created() {
    // STEP 1: Load system configuration
    // Gets bank_system setting for currency display
    await this.getWebSettingInfo()

    // STEP 2: Log admin activity
    // Records that admin accessed the dashboard
    await this.addLogPage()

    // STEP 3: Fetch all dashboard data
    // Loads all API data in parallel for better performance
    await this.fetch_data()
  },

  /**
   * Before destroy lifecycle hook
   * Cleanup when leaving the dashboard
   */
  beforeDestroy() {
    // No intervals to clear since we removed auto-refresh
  },
  
  // ========== METHODS ==========
  methods: {
    /**
     * Fetches web setting information
     * Determines which bank system is being used (TH, KR, etc.)
     * This affects currency display and bank logos
     */
    async getWebSettingInfo() {
      try {
        let settingWebInfo = await this.$store.dispatch('getSettingWebInfo')
        // Default to 'TH' if not set
        this.bankSystem = settingWebInfo?.bank_system[0] || 'TH'
      } catch (e) {
        console.error('Error fetching web settings:', e)
        this.bankSystem = 'TH' // Fallback to Thai system
      }
    },
    
    /**
     * Main data fetching method
     * Fetches all dashboard data from multiple API endpoints
     * Uses Promise.then() for parallel execution
     */
    async fetch_data() {
      try {
        // Check if still on dashboard page
        if (this.$route.name === 'dashboard') {
          
          // ===== FETCH TODAY'S SUMMARY =====
          // Gets today's deposit, withdraw, profit totals
          this.$store.dispatch('dashboardToday').then(result => {
            this.todayReport = result
          })
          
          // ===== FETCH BANK ACCOUNTS =====
          // Gets all bank accounts with balances
          this.$store.dispatch('dashboardAccountbank').then(result => {
            // Process each bank account
            result.forEach((element) => {
              if (element?.bank_code) {
                // Parse bank code to extract country and bank
                const code = element.bank_code
                const bankCodeData = this.checkBankCode(code)
                const { splitCountry, splitBankCode } = bankCodeData
                element.bankCountry = splitCountry  // e.g., 'th'
                element.bank = splitBankCode        // e.g., 'SCB'
              }
            })
            this.accountBankReport = result
          })
          
          // ===== FETCH DEPOSIT TRANSACTIONS =====
          // Gets recent deposit transactions
          this.$store.dispatch('dashboardDeposit').then(result => {
            // Process bank codes for each transaction
            result.forEach((element) => {
              if (element?.bank_code) {
                const code = element.bank_code
                const bankCodeData = this.checkBankCode(code)
                const { splitCountry, splitBankCode } = bankCodeData
                element.bankCountry = splitCountry
                element.bank = splitBankCode
              }
            })
            this.depositReport = result
          })
          
          // ===== FETCH WITHDRAWAL TRANSACTIONS =====
          // Gets recent withdrawal transactions
          this.$store.dispatch('dashboardWithdraw').then(result => {
              result.forEach((element) => {
                if (element?.bank_code) {
                  const code = element.bank_code
                  const bankCodeData = this.checkBankCode(code)
                  const { splitCountry, splitBankCode } = bankCodeData
                  element.bankCountry = splitCountry
                  element.bank = splitBankCode
                }
                // ===== Additional Withdraw Mapping =====
                element.isAdditionalWithdraw = !!(element.is_additional_withdraw)
                if (element.isAdditionalWithdraw && element.additional_withdraw_bank_code) {
                  const addBankData = this.checkBankCode(element.additional_withdraw_bank_code)
                  element.additionalBankCountry = addBankData.splitCountry
                  element.additionalBank = addBankData.splitBankCode
                }
                element.additionalWithdrawBankNumber = element.additional_withdraw_bank_number || null
                element.additionalWithdrawBankName = element.additional_withdraw_bank_name || null
              })
              this.withdrawReport = result
          })
          
          // ===== FETCH DAILY REPORTS =====
          // Gets daily summary for the current month
          this.$store.dispatch('dashboardDaily').then(result => {
            let resData = result
            let totalProfit = 0
            
            // Process each daily report
            for (let i = 0; i < resData.length; i++) {
              if (resData[i]) {
                // Set default for active users if not present
                resData[i].active_users = resData[i]?.active_users || 0
                // Format day to show only day number (1-31)
                resData[i].day = moment(resData[i].day).format('D')
                // Calculate total profit for the month
                totalProfit += parseFloat(resData[i].profit)
              }
            }
            
            this.dailyReports = resData
            this.totalProfit = totalProfit
            // Set current month-year for display
            this.currentMonthYear = moment().format('MM-YY')
          })
          
          // ===== FETCH MONTHLY REPORTS =====
          // Gets monthly summary for the last 12 months
          this.$store.dispatch('dashboardMonthly').then(result => {
            this.monthlyReports = result
            // Format month display (01-24, 02-24, etc.)
            this.monthlyReports.forEach((el, i) => {
              this.monthlyReports[i].month = moment(this.monthlyReports[i].month).format('MM-YY')
            })
          })
          
          // ===== FETCH MEMBER INFO =====
          // Gets member statistics
          this.$store.dispatch('dashboardMemberInfo').then(result => {
            this.memberInfo = result
          })

          // ===== FETCH DAILY OPTIONS (เพิ่มบรรทัดนี้) =====
          await this.fetchDailyOptions()
        }

        // Set current date/time and timezone
        this.tz = this.$store.getters.timezone
        this.currentDate = getFormattedTime(new Date(), this.tz, 'YYYY-MM-DD HH:mm:ss')
        
      } catch (e) {
        console.error('Error fetching dashboard data:', e)
      }
    },
    
    /**
     * Logs admin page access
     * Records IP, username, and page details for audit trail
     */
    async addLogPage() {
      try {
        let data
        const userSystem = store.getters.getuserInfo
        
        // Get user's IP address from external API
        await axios.get('https://api.ipify.org?format=json').then(res =>
          (data = {
            ip: res.data.ip,                            // User's IP address
            name: 'Dashboard',                          // Page name
            url: window.location.href,                  // Full URL
            detail: 'หน้าแรก',                           // Page description
            admin: userSystem.name ? userSystem.name : '', // Admin username
          }),
        )
        
        // Send log to backend
        await this.$store.dispatch('addLogPage', data)
      } catch (e) {
        console.error('Error logging page visit:', e)
      }
    },

    /**
     * Checks and parses bank code format
     * Handles both formats:
     * - With country: "THSCB" -> {country: "th", bank: "SCB"}
     * - Without country: "SCB" -> {country: [from settings], bank: "SCB"}
     * 
     * @param {string} bankCode - Bank code to parse
     * @returns {object} Object containing splitCountry and splitBankCode
     */
    checkBankCode(bankCode) {
      const code = bankCode
      let splitCountry, splitBankCode

      /**
       * Helper function to check if code starts with valid country code
       * @param {string} code - Code to check
       * @returns {boolean} True if starts with valid country code
       */
      const checkCountryCode = (code) => {
        // List of valid country codes
        const validCodes = ['TH', 'KR', 'LA', 'TW', 'CN', 'KH', 'VN', 'MM']
        const firstTwoChars = code.substring(0, 2)
        return validCodes.includes(firstTwoChars)
      }

      if (checkCountryCode(code)) {
        // Has country code prefix (e.g., "THSCB")
        splitCountry = code.slice(0, 2)  // Extract "TH"
        splitBankCode = code.slice(2)    // Extract "SCB"
      } else {
        // No country code, use default from settings
        splitCountry = this.bankSystem   // Use system default
        splitBankCode = code             // Entire code is bank code
      }

      // Convert country to lowercase for image paths
      splitCountry = splitCountry.toLowerCase() // "TH" -> "th"
      
      return { splitCountry, splitBankCode }
    },

    /**
     * ============================================
     * FETCH DAILY OPTIONS
     * ============================================
     * ดึงข้อมูล dropdown options สำหรับเลือกเดือน
     * 
     * API: GET /admin/dashboard/daily/options
     * 
     * Response format:
     * {
     *   options: [
     *     { text: "พ.ย. 2568", value: { year: 2025, month: 11 } },
     *     { text: "ต.ค. 2568", value: { year: 2025, month: 10 } },
     *     ...
     *   ],
     *   default: { year: 2025, month: 11 }
     * }
     * 
     * Called by: fetch_data() เมื่อโหลดหน้า Dashboard
     */
    async fetchDailyOptions() {
      try {
        // เรียก API ผ่าน Vuex action
        const result = await this.$store.dispatch('dashboardDailyOptions')
        
        // เก็บ options สำหรับ v-select dropdown
        // ถ้าไม่มีข้อมูล ใช้ array ว่าง
        this.dailyMonthOptions = result.options || []
        
        // เก็บค่า default (เดือนปัจจุบัน) สำหรับ v-model
        // ถ้าไม่มีข้อมูล ใช้ object ว่าง
        this.dailyDefaultMonth = result.default || {}
        
      } catch (e) {
        console.error('Error fetching daily options:', e)
      }
    },

    /**
     * ============================================
     * ON DAILY MONTH CHANGE
     * ============================================
     * Handler เมื่อ user เปลี่ยนเดือนใน dropdown
     * 
     * API: GET /admin/dashboard/daily/by-month?year=2025&month=10
     * 
     * @param {Object} selected - ค่าที่เลือกจาก dropdown
     * @param {number} selected.year - ปี เช่น 2025
     * @param {number} selected.month - เดือน 1-12
     * 
     * Flow:
     * 1. Validate input
     * 2. Show loading
     * 3. Call API
     * 4. Process data (format day, calculate total)
     * 5. Update state
     * 6. Hide loading
     * 
     * Called by: @month-change event จาก DashboardCardSummaryReport
     */
    async onDailyMonthChange(selected) {
      // ============================================
      // STEP 1: VALIDATE INPUT
      // ============================================
      // ถ้าไม่มี selected หรือไม่มี year/month → ไม่ทำอะไร
      if (!selected || !selected.year || !selected.month) return
      
      // ============================================
      // STEP 2: SHOW LOADING
      // ============================================
      // แสดง loading indicator ใน data table
      this.dailyLoading = true
      
      try {
        // ============================================
        // STEP 3: CALL API
        // ============================================
        // เรียก API ด้วย year และ month ที่เลือก
        const result = await this.$store.dispatch('dashboardDailyByMonth', {
          year: selected.year,
          month: selected.month
        })
        
        // ============================================
        // STEP 4: PROCESS DATA
        // ============================================
        // ถ้าไม่มีข้อมูล ใช้ array ว่าง
        let resData = result || []
        let totalProfit = 0
        
        // Loop ผ่านทุกวัน เพื่อ:
        // - Format วันที่ (YYYY-MM-DD → D)
        // - คำนวณกำไรรวม
        for (let i = 0; i < resData.length; i++) {
          if (resData[i]) {
            // ถ้าไม่มี active_users ให้ใช้ 0
            resData[i].active_users = resData[i]?.active_users || 0
            
            // แปลง "2025-10-15" → "15" (แสดงเฉพาะวันที่)
            resData[i].day = moment(resData[i].day).format('D')
            
            // บวกกำไรของวันนี้เข้า total
            totalProfit += parseFloat(resData[i].profit)
          }
        }
        
        // ============================================
        // STEP 5: UPDATE STATE
        // ============================================
        // อัพเดต data ที่จะแสดงในตาราง
        this.dailyReports = resData
        
        // อัพเดตกำไรรวม (แสดงด้านล่างตาราง)
        this.totalProfit = totalProfit
        
        // อัพเดต label เดือน-ปี เช่น "10-25" (ต.ค. 2025)
        // padStart(2, '0') → เติม 0 ข้างหน้า เช่น 9 → "09"
        // slice(-2) → เอา 2 ตัวท้าย เช่น 2025 → "25"
        this.currentMonthYear = `${String(selected.month).padStart(2, '0')}-${String(selected.year).slice(-2)}`
        
      } catch (e) {
        console.error('Error fetching daily by month:', e)
      } finally {
        // ============================================
        // STEP 6: HIDE LOADING
        // ============================================
        // ซ่อน loading ไม่ว่าจะสำเร็จหรือ error
        this.dailyLoading = false
      }
    },

  },
}
</script>

<!-- ========== STYLES ========== -->
<style scoped>
/* Mobile responsive styles */
@media (max-width: 550px) {
  /* Full width on mobile */
  .dashboard-row {
    width: 100vw !important;
  }

  /* Adjust padding for mobile */
  .dashboard-row .col-12 {
    padding: 6px 0px 6px 8px;
  }

  /* Add margin to first element */
  .dashboard-row .col-12:first-child {
    margin-top: 12px !important;
  }

  /* Add margin to last element */
  .dashboard-row .col-12:last-child {
    margin-bottom: 12px !important;
  }
}
</style>